<template>
  <!-- 导入快录 -->
  <el-dialog
    :title="$t('receivablePayable.BatchImportClips')"
    :visible.sync="visible"
    width="400px"
  >
    <div class="flex-c">
      <el-button
        icon="el-icon-download"
        size="small"
        type="primary"
        :loading="loading"
        style="margin: 0 10px 10px 0"
        @click="downloadTemplate"
      >
        {{ $t('receivablePayable.DownloadTheTemplate') }}
      </el-button>
      <upload-excel
        :on-success="handleImport"
        :but-name="$t('receivablePayable.BatchImportClips')"
      />
    </div>
  </el-dialog>
</template>

<script>
import uploadExcel from '@/components/UploadExcel';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { goFileUploadManagement } from '@/utils/goExportList.js';
import { apiPayableSiteExpenseBatchFastInputFee } from '@/api/finance/receivables';
import i18n from '@/lang';
import { isValidDateTime } from '@/views/finance/payableManage/feesPayable/utils';

// 账单类型对应的文件名
const FILE_NAME_MAP = {
  1: i18n.t('receivablePayable.AnjunCottage'), // 安俊
  2: i18n.t('receivablePayable.ThirdPartyCollection'), // 第三方
  3: i18n.t('receivablePayable.AJDDispatch'), // AJD
  4: i18n.t('receivablePayable.DSPDispatch'), // DSP
  6: i18n.t('receivablePayable.postOfficeDelivery'), // 邮局派送
  7: i18n.t('receivablePayable.MDDDispatch') // MDD
};

// 模板行数
const TEMPLATE_ROW_COUNT = 100;

// 葡语翻译配置
const PT_TRANSLATIONS = {
  waybillNumber: 'número do negócio(QR)',
  customerName: 'cliente',
  siteId: 'Ponto de rede',
  chargeWeight: 'Peso faturável',
  orderBusinessType: 'Tipo de área',
  collectType: 'maneira de coleta',
  operationMode: 'Modelo de operação',
  providerChannelId: 'Canais de provedor de serviços',
  feeCode: 'Tipo de custo',
  fee: 'Valor a Pagar',
  operateTime: 'Hora da ocorrência (Formato: 2022-01-01 00:00:00)',
  operator: 'Pessoa de poeração',
  remark: 'Observações'
};

// 导入时需要提取ID的字段映射
const MAPPED_FIELDS = {
  '网点': 'siteId',
  '业务类型': 'orderBusinessType',
  '揽收方式': 'collectType',
  '运营模式': 'operationMode',
  '费用类型': 'feeCode',
  '操作人(员工账户名)': 'operator',
  '服务商渠道': 'providerChannelId'
};

// 导入时直接映射的字段
const DIRECT_FIELDS = {
  '业务单号(QR)': 'waybillNumber',
  '客户': 'customerName',
  '计费重量': 'chargeWeight',
  '应付金额': 'fee',
  '业务时间(格式：2022-01-01 00:00:00)': 'operateTime',
  '备注': 'remark'
};

// 业务时间字段key
const OPERATE_TIME_KEY = '业务时间(格式：2022-01-01 00:00:00)';

export default {
  name: 'ImportQuickRecording',

  components: {
    uploadExcel
  },

  props: {
    outletsList: {
      type: Array,
      default: () => []
    },
    operatorList: {
      type: Array,
      default: () => []
    },
    feeTypeList: {
      type: Array,
      default: () => []
    },
    providerChannelList: {
      type: Array,
      default: () => []
    },
    payableType: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      visible: false
    };
  },

  methods: {
    /**
     * 打开弹窗
     */
    open() {
      this.visible = true;
    },

    /**
     * 获取基础列配置
     */
    getBaseColumns() {
      return [
        { header: '业务单号(QR)', key: 'waybillNumber', width: 40 },
        { header: '客户', key: 'customerName', width: 40 },
        { header: '网点', key: 'siteId', width: 40 },
        { header: '计费重量', key: 'chargeWeight', width: 40 },
        { header: '费用类型', key: 'feeCode', width: 40 },
        { header: '应付金额', key: 'fee', width: 40 },
        { header: '业务时间(格式：2022-01-01 00:00:00)', key: 'operateTime', width: 80 },
        { header: '操作人(员工账户名)', key: 'operator', width: 40 },
        { header: '备注', key: 'remark', width: 40 }
      ];
    },

    /**
     * 根据账单类型获取扩展列配置
     */
    getExtendColumns() {
      const collectColumns = [
        { header: '揽收方式', key: 'collectType', width: 40 },
        { header: '运营模式', key: 'operationMode', width: 40 }
      ];
      const businessTypeColumn = [{ header: '业务类型', key: 'orderBusinessType', width: 40 }];
      const providerColumn = [{ header: '服务商渠道', key: 'providerChannelId', width: 60 }];

      switch (this.payableType) {
        case 1: return { columns: collectColumns, insertIndex: 4 };
        case 2: return { columns: [...businessTypeColumn, ...collectColumns], insertIndex: 4 };
        case 6: return { columns: providerColumn, insertIndex: 4 };
        default: return null;
      }
    },

    /**
     * 构建完整的列配置
     */
    buildColumns() {
      const columns = this.getBaseColumns();
      const extend = this.getExtendColumns();
      if (extend) {
        columns.splice(extend.insertIndex, 0, ...extend.columns);
      }
      return columns;
    },

    /**
     * 填充下拉数据到工作表
     */
    fillDropdownSheet(sheet, headerKey, dataList, labelProp, valueProp) {
      sheet.columns = [{ header: this.$t(headerKey), key: 'value', width: 40 }];
      const rows = (dataList || []).map(item => ({
        value: `${item[labelProp]}-${item[valueProp]}`
      }));
      sheet.addRows(rows);
    },

    /**
     * 设置单元格下拉验证
     */
    setDropdownValidation(sheet, row, col, refSheet, dataLength) {
      sheet.getCell(row, col).dataValidation = {
        type: 'list',
        formulae: [`=${refSheet}!$A$2:$A${dataLength + 1}`]
      };
    },

    /**
     * 设置葡语标题行
     */
    setTranslatedHeaders(sheet, columns) {
      columns.forEach((col, index) => {
        const cellAddress = `${String.fromCharCode(65 + index)}2`;
        sheet.getCell(cellAddress).value = PT_TRANSLATIONS[col.key] || '';
      });
    },

    /**
     * 设置提示信息行
     */
    setTipsRow(sheet) {
      sheet.mergeCells('A3:K3');
      sheet.getCell('A3').value =
        '1、每次仅导入 100000 条 /Importar apenas 100000 itens de cada vez\n' +
        '2、除了备注其他必填 /Tudo, exceto as notas, é obrigatório\n' +
        '3、不可更改此模板 /Esse modelo não pode ser alterado';
      sheet.getRow(3).getCell(1).font = {
        color: { argb: 'ff0000' }
      };
    },

    /**
     * 获取操作人数据
     */
    getOperatorData() {
      return this.operatorList.map(item => ({
        operator: this.payableType === 3
          ? `${item.deliveryManName}-${item.supplierId}`
          : `${item.username}-${item.id}`
      }));
    },

    /**
     * 获取服务商渠道数据
     */
    getProviderData() {
      return this.providerChannelList.map(item => ({
        providerChannelId: `${item.providerChannelName}-${item.id}`
      }));
    },

    /**
     * 根据账单类型设置行数据验证
     */
    setRowValidations(mainSheet, row, dataLengths) {
      const { outlets, feeType, operator, provider, businessType, collect, operation } = dataLengths;

      // 网点（所有类型都需要）
      this.setDropdownValidation(mainSheet, row, 3, 'Sheet2', outlets);

      switch (this.payableType) {
        case 1: // 安骏
          this.setDropdownValidation(mainSheet, row, 5, 'Sheet4', collect);
          this.setDropdownValidation(mainSheet, row, 6, 'Sheet5', operation);
          this.setDropdownValidation(mainSheet, row, 7, 'Sheet6', feeType);
          this.setDropdownValidation(mainSheet, row, 10, 'Sheet7', operator);
          mainSheet.getCell(row, 9).numFmt = '@';
          break;
        case 2: // 第三方
          this.setDropdownValidation(mainSheet, row, 5, 'Sheet3', businessType);
          this.setDropdownValidation(mainSheet, row, 6, 'Sheet4', collect);
          this.setDropdownValidation(mainSheet, row, 7, 'Sheet5', operation);
          this.setDropdownValidation(mainSheet, row, 8, 'Sheet6', feeType);
          this.setDropdownValidation(mainSheet, row, 11, 'Sheet7', operator);
          mainSheet.getCell(row, 10).numFmt = '@';
          break;
        case 6: // 邮局
          this.setDropdownValidation(mainSheet, row, 5, 'Sheet8', provider);
          this.setDropdownValidation(mainSheet, row, 6, 'Sheet6', feeType);
          this.setDropdownValidation(mainSheet, row, 9, 'Sheet7', operator);
          mainSheet.getCell(row, 8).numFmt = '@';
          break;
        default: // AJD/DSP/MDD
          this.setDropdownValidation(mainSheet, row, 5, 'Sheet6', feeType);
          this.setDropdownValidation(mainSheet, row, 8, 'Sheet7', operator);
          mainSheet.getCell(row, 7).numFmt = '@';
      }
    },

    /**
     * 下载模板
     */
    async downloadTemplate() {
      this.loading = true;
      try {
        const wb = new ExcelJS.Workbook();

        // 创建工作表
        const mainSheet = wb.addWorksheet('Sheet1');
        const outletsSheet = wb.addWorksheet('Sheet2');
        const businessTypeSheet = wb.addWorksheet('Sheet3');
        const collectTypeSheet = wb.addWorksheet('Sheet4');
        const operationModeSheet = wb.addWorksheet('Sheet5');
        const feeTypeSheet = wb.addWorksheet('Sheet6');
        const operatorSheet = wb.addWorksheet('Sheet7');
        const providerSheet = wb.addWorksheet('Sheet8');

        // 设置主表列
        const columns = this.buildColumns();
        mainSheet.columns = columns;

        // 设置葡语标题和提示
        this.setTranslatedHeaders(mainSheet, columns);
        this.setTipsRow(mainSheet);

        // 填充下拉数据表
        this.fillDropdownSheet(outletsSheet, 'basicData.Outlets', this.outletsList, 'name', 'id');
        this.fillDropdownSheet(feeTypeSheet, 'basicData.ExpenseType', this.feeTypeList, 'displayName', 'code');

        // 业务类型
        businessTypeSheet.columns = [{ header: this.$t('collectionCenter.businessT'), key: 'orderBusinessType', width: 40 }];
        businessTypeSheet.addRows([
          { orderBusinessType: `${this.$t('basicData.CourierSystemBusiness')}-1` },
          { orderBusinessType: `${this.$t('basicData.CollectionSystemBusiness')}-0` }
        ]);

        // 揽收方式
        collectTypeSheet.columns = [{ header: this.$t('collectionCenter.lanshouTypee'), key: 'collectType', width: 40 }];
        collectTypeSheet.addRows([
          { collectType: `${this.$t('collectionCenter.dropOff')}-1` },
          { collectType: `${this.$t('collectionCenter.doorPickUp')}-2` }
        ]);

        // 运营模式
        operationModeSheet.columns = [{ header: this.$t('collectionCenter.operationMode'), key: 'operationMode', width: 40 }];
        operationModeSheet.addRows([
          { operationMode: `${this.$t('collectionCenter.directOperation')}-0` },
          { operationMode: `${this.$t('collectionCenter.thirdPartyCooperation')}-1` }
        ]);

        // 操作人
        const operatorData = this.getOperatorData();
        operatorSheet.columns = [{ header: this.$t('basicData.Operator'), key: 'operator', width: 40 }];
        operatorSheet.addRows(operatorData);

        // 服务商渠道
        const providerData = this.getProviderData();
        providerSheet.columns = [{ header: this.$t('GlobalSetObj.ServiceProviderChannels'), key: 'providerChannelId', width: 60 }];
        providerSheet.addRows(providerData);

        // 数据长度配置
        const dataLengths = {
          outlets: this.outletsList.length,
          feeType: this.feeTypeList.length,
          operator: operatorData.length,
          provider: providerData.length,
          businessType: 2,
          collect: 2,
          operation: 2
        };

        // 设置数据验证
        for (let i = 0; i < TEMPLATE_ROW_COUNT; i++) {
          this.setRowValidations(mainSheet, i + 3, dataLengths);
        }

        // 导出文件
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, this.getFileName());
      } finally {
        this.loading = false;
      }
    },

    /**
     * 解析单行数据
     */
    parseRowData(row, rowIndex) {
      const data = {};

      Object.keys(row).forEach(key => {
        const value = row[key];

        // 需要提取ID的字段
        if (MAPPED_FIELDS[key]) {
          data[MAPPED_FIELDS[key]] = value?.split('-').pop() || '';
          return;
        }

        // 业务时间需要校验格式
        if (key === OPERATE_TIME_KEY) {
          if (!isValidDateTime(value)) {
            const rowInfo = `${this.$t('basicData.row')}${rowIndex + 1}${this.$t('basicData.line')}`;
            const errorMsg = this.$t('receivablePayable.BusinessTimeFormatError');
            throw new Error(`${rowInfo} ${errorMsg}: ${value}`);
          }
          data[DIRECT_FIELDS[key]] = value;
          return;
        }

        // 直接映射的字段
        if (DIRECT_FIELDS[key]) {
          data[DIRECT_FIELDS[key]] = value || '';
        }
      });

      return data;
    },

    /**
     * 处理导入数据
     */
    async handleImport(importData) {
      try {
        // 跳过前两行（标题行）
        const importList = importData[0].results.slice(2);

        // 解析数据
        const dataList = importList.map((row, index) => {
          try {
            return this.parseRowData(row, index);
          } catch (error) {
            this.$message.error(error.message);
            throw error;
          }
        });

        // 添加业务类型
        const list = dataList.map(item => ({
          ...item,
          businessType: [1, 2].includes(this.payableType) ? 2 : 3
        }));

        // 提交数据
        const res = await apiPayableSiteExpenseBatchFastInputFee({
          list,
          payableType: this.payableType
        });

        if (res.code === 200) {
          await goFileUploadManagement(this);
          this.visible = false;
        }
      } catch (error) {
        // 错误已在 parseRowData 中处理
        console.error(error);
      }
    },

    /**
     * 获取模板文件名
     */
    getFileName() {
      return `${FILE_NAME_MAP[this.payableType]}${this.$t('receivablePayable.BatchImportClips')}.xlsx`;
    }
  }
};
</script>

<style scoped>
.flex-c {
  display: flex;
  flex-wrap: wrap;
}
</style>
